import { PostgresQueryCompiler, QueryAst } from "@avenueops/visual-query-editor";
import { Button, Modal, SmartForm, ToggleGroup } from "@avenueops/web-components";
import { DataViewType, WorkflowAlertingConditions } from "@prisma/client";
import QueryBuilder from "components/molecules/queryBuilder";
import { useQueryColumns } from "context/QueryRunContext";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useController, useFormContext, useWatch } from "react-hook-form";

interface Props {
  enrichmentOnly?: boolean;
  visualQueryEditor?: boolean;
}

export default function QuerySection({ enrichmentOnly, visualQueryEditor }: Props) {
  const { watch } = useFormContext();
  const [showQueryTypeToggleWarning, setShowQueryTypeToggleWarning] = useState(false);

  const alertingConditions = watch("alertingConditions");
  const queryColumns = useQueryColumns();

  const queryTypeProps = useController({ name: "dataViewType" });
  const visualQueryBlob = useWatch({ name: "visualQueryBlob" });
  const queryProps = useController({ name: "query" });

  useEffect(() => {
    if (!visualQueryEditor) {
      return;
    }
    let timeout: NodeJS.Timeout;
    if (visualQueryBlob) {
      queryProps.field.onChange(null);
      timeout = setTimeout(() => {
        const VisQuery = new PostgresQueryCompiler(visualQueryBlob as QueryAst);
        const validation = VisQuery.validate();
        if (validation.success) {
          const newQuery = VisQuery.compile();
          queryProps.field.onChange(newQuery);
        }
      }, 500);
    } else {
      queryProps.field.onChange(null);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [visualQueryBlob]);

  return (
    <div className={visualQueryEditor ? "mx-4 flex flex-col gap-10" : "lg:mx-16 md:mx-10 flex flex-col gap-10"}>
      {visualQueryEditor ? (
        <div className="w-full flex justify-end">
          <div className="relative">
            <ToggleGroup
              name="dataViewType"
              type="RADIO"
              selectedValue={queryTypeProps.field.value}
              onChange={() => {
                setShowQueryTypeToggleWarning(true);
              }}
              options={[
                {
                  label: "SQL Editor",
                  value: DataViewType.SQL,
                },
                {
                  label: "Visual Editor",
                  value: DataViewType.VISUAL,
                },
              ]}
            />
          </div>
        </div>
      ) : null}
      <Modal
        open={showQueryTypeToggleWarning}
        onClose={() => setShowQueryTypeToggleWarning(false)}
        cancelActionTrigger={
          <Button key="cancel_button" type="PRIMARY" onClick={() => setShowQueryTypeToggleWarning(false)}>
            Return to Visual Query Editor
          </Button>
        }
        actions={[
          <Button
            key="continue_with_sql"
            type="DANGER"
            onClick={() => {
              queryTypeProps.field.onChange(DataViewType.SQL);
              setShowQueryTypeToggleWarning(false);
            }}
          >
            Continue with SQL
          </Button>,
        ]}
      >
        <div className="flex flex-col gap-2">
          <p className="text-gray-900 text-xl">Warning</p>
          <p>Changing the query type to raw SQL is a permanent change! If you continue, you will not be able edit your query in the visual query editor.</p>
          <p>Are you sure you want to continue?</p>
        </div>
      </Modal>
      <QueryBuilder visualQueryEditor={visualQueryEditor} />
      {alertingConditions !== WorkflowAlertingConditions.PASSTHROUGH &&
      alertingConditions !== WorkflowAlertingConditions.METRIC_THRESHOLD &&
      !enrichmentOnly ? (
        <div className="w-full flex gap-2 flex-col md:flex-row">
          <div className="flex-1">
            <div className="font-medium text-gray-600">Which columns uniquely identify a row?</div>
            <label className="items-center leading-5 text-sm text-gray-500 mb-2">Avenue will use this column's value to determine if a row is new.</label>
          </div>
          <div className="flex-1">
            <SmartForm.DropdownMultiSelect
              name="idColumns"
              placeholder="Enter or select..."
              options={queryColumns.map((column) => ({ value: column, label: column }))}
              onCreate={(item) => {
                return { value: item, label: item };
              }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
